import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import AnalystLeaderboard from '../components/analysts/AnalystLeaderboard';
import AnalystSubmitForm from '../components/analysts/AnalystSubmitForm';
import Pagination from '../components/Pagination';
import { SlidersHorizontal } from 'lucide-react';
import ListLimitBanner from '../components/ui/ListLimitBanner';

const PAGE_SIZE = 30;

const PERIODS = [
  { value: 'all', label: 'All-time' },
  { value: '7d',  label: '7D' },
  { value: '1m',  label: '1M' },
  { value: '3m',  label: '3M' },
  { value: '6m',  label: '6M' },
  { value: '1y',  label: '1Y' },
];

const PERIOD_FIELD = {
  all: 'overall_return_pct',
  '7d': 'avg_return_7d',
  '1m': 'avg_return_1m',
  '3m': 'avg_return_3m',
  '6m': 'avg_return_6m',
  '1y': 'avg_return_1y',
};

const PERIOD_ORDER = {
  all: '-overall_return_pct',
  '7d': '-avg_return_7d',
  '1m': '-avg_return_1m',
  '3m': '-avg_return_3m',
  '6m': '-avg_return_6m',
  '1y': '-avg_return_1y',
};

function getAuthHeader() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function LeaderboardPage() {
  const location = useLocation();
  const [analysts, setAnalysts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [assetClass, setAssetClass] = useState('');
  const [exchange, setExchange] = useState('');
  const [exchanges, setExchanges] = useState([]);
  const [ordering, setOrdering] = useState('leaderboard_score');
  const [returnPeriod, setReturnPeriod] = useState('all');
  const [userPickedSort, setUserPickedSort] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [analystsListLimit, setAnalystsListLimit] = useState(null);

  // Sector/industry filter (can be pre-populated from URL params)
  const [sector, setSector] = useState(() => new URLSearchParams(location.search).get('sector') || '');
  const [industry, setIndustry] = useState(() => new URLSearchParams(location.search).get('industry') || '');
  const [sectors, setSectors] = useState([]);
  const [industries, setIndustries] = useState([]);

  // Advanced threshold filters
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [minPredictions, setMinPredictions] = useState('20');
  const [minReturnPct, setMinReturnPct] = useState('');
  const [stagedMinPredictions, setStagedMinPredictions] = useState('20');
  const [stagedMinReturnPct, setStagedMinReturnPct] = useState('');

  const activeAdvancedCount = [minPredictions, minReturnPct].filter(Boolean).length;
  const activeFilterCount = [assetClass, exchange, sector, industry].filter(Boolean).length + activeAdvancedCount;
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  // Load sector list + exchange list once on mount
  useEffect(() => {
    axios.get(`${API_BASE_URL}/api/research/symbols/filter-options/`)
      .then((res) => setExchanges(res.data.exchanges ?? []))
      .catch(() => {});
    axios.get(`${API_BASE_URL}/api/research/sectors/`)
      .then((res) => setSectors(res.data))
      .catch(() => {});
    const initSector = new URLSearchParams(location.search).get('sector');
    if (initSector) {
      axios.get(`${API_BASE_URL}/api/research/sectors/${encodeURIComponent(initSector)}/industries/`)
        .then((res) => setIndustries(res.data))
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load industries when sector changes
  useEffect(() => {
    if (!sector) { setIndustries([]); return; }
    axios.get(`${API_BASE_URL}/api/research/sectors/${encodeURIComponent(sector)}/industries/`)
      .then((res) => setIndustries(res.data))
      .catch(() => setIndustries([]));
  }, [sector]);

  // When a period chip is selected, auto-switch sort unless user manually picked one
  function handlePeriodChange(period) {
    setReturnPeriod(period);
    if (!userPickedSort) {
      setOrdering(period === 'all' ? 'leaderboard_score' : PERIOD_ORDER[period]);
    }
    setPage(1);
  }

  function handleSortChange(value) {
    setOrdering(value);
    setUserPickedSort(true);
    setPage(1);
  }

  useEffect(() => { setPage(1); }, [search, assetClass, exchange, sector, industry, minPredictions, minReturnPct]);

  function applyAdvancedFilters() {
    setMinPredictions(stagedMinPredictions);
    setMinReturnPct(stagedMinReturnPct);
    setPage(1);
  }

  function clearAdvancedFilters() {
    setStagedMinPredictions('');
    setStagedMinReturnPct('');
    setMinPredictions('');
    setMinReturnPct('');
  }

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set('ordering', ordering);
    if (search) params.set('search', search);
    if (assetClass) params.set('asset_class', assetClass);
    if (exchange) params.set('exchange', exchange);
    if (sector) params.set('sector', sector);
    if (industry) params.set('industry', industry);
    if (minPredictions) params.set('min_predictions', minPredictions);
    // Period-aware min return
    if (minReturnPct) {
      if (returnPeriod === 'all') {
        params.set('min_return_pct', minReturnPct);
      } else {
        params.set('period', returnPeriod);
        params.set('min_return', minReturnPct);
      }
    }
    params.set('page', page);
    params.set('page_size', PAGE_SIZE);

    axios
      .get(`${API_BASE_URL}/api/research/analysts/?${params}`, { headers: getAuthHeader() })
      .then((res) => {
        const data = res.data;
        if (data && 'results' in data) {
          setAnalysts(data.results);
          setTotalCount(data.count ?? 0);
          if (data.list_limit_reached) {
            setAnalystsListLimit({ limit: data.list_limit, tier: data.list_limit_tier });
          } else {
            setAnalystsListLimit(null);
          }
        } else {
          setAnalysts(Array.isArray(data) ? data : []);
          setTotalCount(Array.isArray(data) ? data.length : 0);
          setAnalystsListLimit(null);
        }
      })
      .catch(() => { setAnalysts([]); setTotalCount(0); setAnalystsListLimit(null); })
      .finally(() => setLoading(false));
  }, [search, assetClass, exchange, sector, industry, ordering, returnPeriod, minPredictions, minReturnPct, page]);

  const rankOffset = (page - 1) * PAGE_SIZE;
  const minReturnLabel = returnPeriod === 'all' ? 'Min Return %' : `Min ${returnPeriod.toUpperCase()} Return %`;

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Leaderboard</h1>
        <p className="text-sm text-gray-500 mt-1">
          All tracked analysts ranked by performance, updated in real time.
        </p>
      </div>

      <div className="mb-5 max-w-md">
        <AnalystSubmitForm />
      </div>

      {/* Mobile: search + filter toggle row */}
      <div className="flex md:hidden gap-2 mb-3">
        <input
          type="text"
          placeholder="Search analysts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
        />
        <button
          onClick={() => setFiltersOpen((v) => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors flex-shrink-0 ${
            activeFilterCount > 0
              ? 'bg-gray-900 text-white border-gray-900'
              : 'bg-white text-gray-600 border-gray-200'
          }`}
        >
          <SlidersHorizontal size={14} />
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-1 bg-white text-gray-900 text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {/* Desktop: full filter row */}
      <div className="hidden md:flex flex-wrap gap-3 mb-4">
        <input
          type="text"
          placeholder="Search analysts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[180px] px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
        />

        <select
          value={assetClass}
          onChange={(e) => setAssetClass(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
        >
          <option value="">All Asset Classes</option>
          {['Equities', 'Crypto', 'Options', 'Commodities', 'Macro'].map((cls) => (
            <option key={cls} value={cls}>{cls}</option>
          ))}
        </select>

        <select
          value={exchange}
          onChange={(e) => setExchange(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
        >
          <option value="">All Exchanges</option>
          {exchanges.map((ex) => (
            <option key={ex} value={ex}>{ex}</option>
          ))}
        </select>

        <select
          value={sector}
          onChange={(e) => { setSector(e.target.value); setIndustry(''); setPage(1); }}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
        >
          <option value="">All Sectors</option>
          {sectors.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        {(sector || industry) && (
          <select
            value={industry}
            onChange={(e) => { setIndustry(e.target.value); setPage(1); }}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
          >
            <option value="">All Industries</option>
            {industry && !industries.includes(industry) && (
              <option value={industry}>{industry}</option>
            )}
            {industries.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        )}

        <select
          value={ordering}
          onChange={(e) => handleSortChange(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
        >
          <option value="leaderboard_score">Best Overall</option>
          <option value="-overall_return_pct">All-time Return (High)</option>
          <option value="-avg_return_7d">7D Return (High)</option>
          <option value="-avg_return_1m">1M Return (High)</option>
          <option value="-avg_return_3m">3M Return (High)</option>
          <option value="-avg_return_6m">6M Return (High)</option>
          <option value="-avg_return_1y">1Y Return (High)</option>
          <option value="-predictions_count">Most Predictions</option>
          <option value="-created_at">Newest</option>
          <option value="name">Name (A-Z)</option>
        </select>

        <button
          onClick={() => setShowAdvanced((v) => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
            activeAdvancedCount > 0
              ? 'bg-gray-900 text-white border-gray-900'
              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
          }`}
        >
          <SlidersHorizontal size={14} />
          Advanced
          {activeAdvancedCount > 0 && (
            <span className="ml-1 bg-white text-gray-900 text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {activeAdvancedCount}
            </span>
          )}
        </button>
      </div>

      {/* Mobile expanded filter panel */}
      {filtersOpen && (
        <div className="md:hidden flex flex-col gap-3 mb-4 p-4 bg-white border border-gray-200 rounded-xl">
          <select
            value={assetClass}
            onChange={(e) => setAssetClass(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
          >
            <option value="">All Asset Classes</option>
            {['Equities', 'Crypto', 'Options', 'Commodities', 'Macro'].map((cls) => (
              <option key={cls} value={cls}>{cls}</option>
            ))}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <select
              value={exchange}
              onChange={(e) => setExchange(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              <option value="">All Exchanges</option>
              {exchanges.map((ex) => (
                <option key={ex} value={ex}>{ex}</option>
              ))}
            </select>
            <select
              value={sector}
              onChange={(e) => { setSector(e.target.value); setIndustry(''); setPage(1); }}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              <option value="">All Sectors</option>
              {sectors.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {(sector || industry) && (
              <select
                value={industry}
                onChange={(e) => { setIndustry(e.target.value); setPage(1); }}
                className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
              >
                <option value="">All Industries</option>
                {industry && !industries.includes(industry) && (
                  <option value={industry}>{industry}</option>
                )}
                {industries.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            )}
            <select
              value={ordering}
              onChange={(e) => handleSortChange(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
            >
              <option value="leaderboard_score">Best Overall</option>
              <option value="-overall_return_pct">All-time Return (High)</option>
              <option value="-avg_return_7d">7D Return (High)</option>
              <option value="-avg_return_1m">1M Return (High)</option>
              <option value="-avg_return_3m">3M Return (High)</option>
              <option value="-avg_return_6m">6M Return (High)</option>
              <option value="-avg_return_1y">1Y Return (High)</option>
              <option value="-predictions_count">Most Predictions</option>
              <option value="-created_at">Newest</option>
              <option value="name">Name (A-Z)</option>
            </select>
          </div>
          <button
            onClick={() => setShowAdvanced((v) => !v)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors w-fit ${
              activeAdvancedCount > 0
                ? 'bg-gray-900 text-white border-gray-900'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
            }`}
          >
            <SlidersHorizontal size={14} />
            Advanced
            {activeAdvancedCount > 0 && (
              <span className="ml-1 bg-white text-gray-900 text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">
                {activeAdvancedCount}
              </span>
            )}
          </button>
        </div>
      )}

      {/* Period chip row */}
      <div className="flex items-center gap-1.5 mb-4 overflow-x-auto pb-1">
        {PERIODS.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => handlePeriodChange(value)}
            className={`flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
              returnPeriod === value
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
            }`}
          >
            {label}
          </button>
        ))}
        <span className="ml-2 text-xs text-gray-400 flex-shrink-0 hidden sm:inline">Return window shown on cards</span>
      </div>

      {/* Advanced filter panel */}
      {showAdvanced && (
        <div className="flex flex-wrap items-end gap-4 mb-4 p-4 bg-white border border-gray-200 rounded-xl">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">Min Predictions</label>
            <input
              type="number"
              min="0"
              placeholder="e.g. 20"
              value={stagedMinPredictions}
              onChange={(e) => setStagedMinPredictions(e.target.value)}
              className="w-28 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-gray-500">{minReturnLabel}</label>
            <input
              type="number"
              placeholder="e.g. 10"
              value={stagedMinReturnPct}
              onChange={(e) => setStagedMinReturnPct(e.target.value)}
              className="w-28 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={applyAdvancedFilters}
              className="px-4 py-1.5 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Apply
            </button>
            <button
              onClick={clearAdvancedFilters}
              className="px-4 py-1.5 text-sm font-medium bg-white text-gray-600 border border-gray-200 rounded-lg hover:border-gray-400 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Active filter summary bar */}
      {(assetClass || exchange || sector || industry || returnPeriod !== 'all' || activeAdvancedCount > 0) && (
        <div className="flex items-center gap-2 mb-4 text-sm text-gray-600">
          <span className="text-gray-400">Showing results for:</span>
          <div className="flex items-center gap-1.5 flex-wrap">
            {assetClass && <span className="font-medium">{assetClass}</span>}
            {exchange && <><span className="text-gray-300">·</span><span className="font-medium">{exchange}</span></>}
            {sector && <><span className="text-gray-300">·</span><span className="font-medium">{sector}</span></>}
            {industry && <><span className="text-gray-300">·</span><span className="font-medium">{industry}</span></>}
            {returnPeriod !== 'all' && <><span className="text-gray-300">·</span><span className="font-medium">{returnPeriod.toUpperCase()} Return</span></>}
            {minPredictions && <><span className="text-gray-300">·</span><span className="font-medium">Min {minPredictions} predictions</span></>}
            {minReturnPct && <><span className="text-gray-300">·</span><span className="font-medium">Min {minReturnPct}% {returnPeriod !== 'all' ? returnPeriod.toUpperCase() + ' ' : ''}return</span></>}
          </div>
          <button
            onClick={() => {
              setAssetClass('');
              setCountry('');
              setSector('');
              setIndustry('');
              setReturnPeriod('all');
              setUserPickedSort(false);
              setOrdering('leaderboard_score');
              clearAdvancedFilters();
            }}
            className="ml-auto text-xs text-gray-400 hover:text-gray-700 underline"
          >
            Clear all
          </button>
        </div>
      )}

      <AnalystLeaderboard
        analysts={analysts}
        loading={loading}
        rankOffset={rankOffset}
        showFollow
        returnPeriod={returnPeriod}
        periodField={PERIOD_FIELD}
      />
      <Pagination page={page} totalPages={totalPages} onPage={setPage} />
      {analystsListLimit && (
        <ListLimitBanner limit={analystsListLimit.limit} type="analyst" />
      )}
    </div>
  );
}
